import { useEffect, useState } from "react";
import { useModules } from "../context/ModulesContext";

// Reusable hook: returns the list of org users who have access to the module
// rendered at `moduleRoute`. Module is identified by its registered route —
// the most stable field on a module record, since routes are wired in App.js
// and don't drift the way registry keys can.
//
// Access rules:
//   • Owner role (system role with implicit full access) → always included
//   • Custom role → included if role.permissions contains any of the
//     module's access keys (viewPermission, entitlementKey, or module.key —
//     the union covers the various naming conventions different role-editor
//     flows have written into role.permissions over time)
//
// The hook also listens for window focus and refetches, so role/permission
// edits made elsewhere surface without a full app reload.

export interface ModuleAccessUser {
  id: string;
  name: string;
  email: string;
}

const USERS_API = `${process.env.REACT_APP_BACKEND_URL}/approved_users`;
const ROLES_API = `${process.env.REACT_APP_BACKEND_URL}/roles?scope=org`;

export function useUsersWithModuleAccess(
  moduleRoute: string,
): ModuleAccessUser[] {
  const [users, setUsers] = useState<ModuleAccessUser[]>([]);
  const { modules, ready: modulesReady } = useModules();

  useEffect(() => {
    if (!modulesReady) return;

    // Locate the module by route — the canonical identifier we control in
    // App.js. Module keys can drift; routes don't.
    const targetModule = modules.find((m) => m.route === moduleRoute);

    // Permission keys constituting "has access". Different role-editor flows
    // have stored access under different naming conventions; accept the
    // union so the filter doesn't break on naming drift between registry
    // and role docs.
    const accessKeys = new Set<string>(
      [
        targetModule?.viewPermission,
        targetModule?.entitlementKey,
        targetModule?.key,
      ].filter((k): k is string => !!k && typeof k === "string"),
    );

    let cancelled = false;

    const loadUsers = () => {
      Promise.all([
        fetch(USERS_API, { credentials: "include" }).then((r) =>
          r.ok ? r.json() : [],
        ),
        fetch(ROLES_API, { credentials: "include" }).then((r) =>
          r.ok ? r.json() : { org: [] },
        ),
      ])
        .then(([usersData, rolesData]) => {
          if (cancelled) return;
          const orgRoles: Array<{
            id: string;
            name?: string;
            isSystem?: boolean;
            permissions?: string[];
          }> = Array.isArray(rolesData?.org) ? rolesData.org : [];
          const rolesById = new Map<
            string,
            { isOwner: boolean; permissions: Set<string> }
          >();
          for (const r of orgRoles) {
            rolesById.set(r.id, {
              isOwner: !!r.isSystem && r.name === "Owner",
              permissions: new Set(r.permissions || []),
            });
          }
          const apiUsers: Array<{
            user_id?: string;
            name?: string;
            email?: string;
            customRoles?: string[];
          }> = Array.isArray(usersData) ? usersData : [];
          const filtered: ModuleAccessUser[] = apiUsers
            .filter((u) => {
              const roleIds = Array.isArray(u.customRoles)
                ? u.customRoles
                : [];
              return roleIds.some((rid) => {
                const role = rolesById.get(rid);
                if (!role) return false;
                // Owner role grants module access regardless of which
                // permission keys are recorded on the role document.
                if (role.isOwner) return true;
                // Otherwise: any of the module's access keys must be ticked.
                for (const k of accessKeys) {
                  if (role.permissions.has(k)) return true;
                }
                return false;
              });
            })
            .map((u) => ({
              id: u.user_id || "",
              name: u.name || u.email || u.user_id || "",
              email: u.email || "",
            }))
            .filter((u) => u.id);
          setUsers(filtered);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error(
            `[useUsersWithModuleAccess] failed to load users for ${moduleRoute}:`,
            err,
          );
          setUsers([]);
        });
    };

    loadUsers();

    // Refetch on tab refocus so role/permission edits made elsewhere
    // surface without requiring a full app reload.
    const onFocus = () => loadUsers();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [modulesReady, modules, moduleRoute]);

  return users;
}
